/**
 * /financeiro/cheques — Controle de Cheques (FIN-8 — ADR-0042).
 *
 * Fiel ao mockup Fase 5 V2.2 (#cheques): KPI strip (em caixa / a compensar /
 * repassados / sustados-devolvidos) + tabchips de filtro + dtable + slide-over
 * "Novo cheque" + painel de ações por situação (depositar/compensar/repassar/
 * devolver/sustar). Consome /api/cheques (backend novo deste sprint).
 */
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { FiAlertTriangle, FiPlus } from "react-icons/fi";

import AsyncButton from "../../../components/AsyncButton";
import SlideOverPanel from "../../../components/SlideOverPanel";
import { useAuth } from "../../../context/AuthContext";
import { listarClientes } from "../../../services/comercial";
import {
  formatarReais, listarContas, listarLancamentos,
} from "../../../services/financeiro";
import { listarPedidos } from "../../../services/pedidos";
import {
  compensarCheque, criarCheque, depositarCheque, devolverCheque,
  listarCheques, obterResumoCheques, repassarCheque, sustarCheque,
} from "../../../services/cheques";
import { PageContainer } from "../../../styles/shared";
import { Input, Select } from "../../Orcamentos/styles";

function isoLocal(d) {
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
}
function ddmm(iso) {
  if (!iso) return "—";
  const [, m, d] = iso.split("-");
  return `${d}/${m}`;
}
function mesAno(iso) {
  if (!iso) return "—";
  const [y, m] = iso.split("-");
  return `${m}/${y}`;
}

const TABS = [
  { id: "todos", label: "Todos", match: () => true },
  { id: "em_caixa", label: "Em caixa", match: (c) => c.situacao === "EM_CAIXA" },
  { id: "a_compensar", label: "A compensar", match: (c) => c.situacao === "DEPOSITADO" },
  { id: "compensados", label: "Compensados", match: (c) => c.situacao === "COMPENSADO" },
  { id: "sustados", label: "Sustados", match: (c) => c.situacao === "SUSTADO" || c.situacao === "DEVOLVIDO" },
];

const TONE = {
  EM_CAIXA: "neutral", DEPOSITADO: "info", COMPENSADO: "success",
  REPASSADO: "neutral", DEVOLVIDO: "danger", SUSTADO: "danger",
};

function destinoTexto(c) {
  if (c.situacao === "REPASSADO") return `Repassado · ${c.fornecedor_nome || "—"}`;
  if (c.situacao === "DEPOSITADO" || c.situacao === "COMPENSADO") {
    return c.conta_deposito_nome ? `Depositado · ${c.conta_deposito_nome}` : "Depositado";
  }
  if (c.situacao === "DEVOLVIDO") return `Devolvido${c.motivo ? ` · ${c.motivo.slice(0, 22)}` : ""}`;
  if (c.situacao === "SUSTADO") return "Sustado";
  return "Em caixa";
}


function NovoChequeForm({ clientes, contas, onSalvar, onCancel }) {
  const { request } = useAuth();
  const hoje = isoLocal(new Date());
  const [d, setD] = useState({
    cliente_id: "", valor: "", data_recebimento: hoje, bom_para: "",
    numero_cheque: "", banco_emissor: "", situacao_inicial: "EM_CAIXA",
    observacoes: "", pedido_id: "", lancamento_id: "", conta_deposito_id: "",
  });
  const set = (k, v) => setD((p) => ({ ...p, [k]: v }));
  const valido = d.cliente_id && Number(d.valor) > 0 && d.data_recebimento && d.bom_para;

  // Pedidos do cliente e parcelas em aberto do pedido: e o vinculo que faz
  // compensar BAIXAR a conta a receber em vez de criar uma entrada nova
  // (que contaria o mesmo dinheiro duas vezes).
  const [pedidos, setPedidos] = useState([]);
  const [parcelas, setParcelas] = useState([]);

  useEffect(() => {
    if (!d.cliente_id) { setPedidos([]); return; }
    listarPedidos(request, { cliente: d.cliente_id, pageSize: 100 })
      .then((p) => setPedidos(Array.isArray(p) ? p : p?.results || []))
      .catch(() => setPedidos([]));
  }, [request, d.cliente_id]);

  useEffect(() => {
    if (!d.pedido_id) { setParcelas([]); return; }
    listarLancamentos(request, {
      pedido: d.pedido_id, tipo: "ENTRADA", confirmado: false,
    })
      .then((p) => setParcelas(Array.isArray(p) ? p : p?.results || []))
      .catch(() => setParcelas([]));
  }, [request, d.pedido_id]);
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
      <label style={{ display: "flex", flexDirection: "column", gap: 4 }}>
        <span style={{ fontSize: 12, fontWeight: 600 }}>Cliente *</span>
        <Select value={d.cliente_id} onChange={(e) => set("cliente_id", e.target.value)}>
          <option value="">— Selecione o cliente —</option>
          {clientes.map((c) => <option key={c.id} value={c.id}>{c.nome}</option>)}
        </Select>
      </label>
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
        <label style={{ display: "flex", flexDirection: "column", gap: 4 }}>
          <span style={{ fontSize: 12, fontWeight: 600 }}>Nº do cheque</span>
          <Input value={d.numero_cheque} onChange={(e) => set("numero_cheque", e.target.value)} placeholder="000000" />
        </label>
        <label style={{ display: "flex", flexDirection: "column", gap: 4 }}>
          <span style={{ fontSize: 12, fontWeight: 600 }}>Valor *</span>
          <Input type="number" step="0.01" value={d.valor} onChange={(e) => set("valor", e.target.value)} placeholder="0,00" />
        </label>
        <label style={{ display: "flex", flexDirection: "column", gap: 4 }}>
          <span style={{ fontSize: 12, fontWeight: 600 }}>Data de recebimento *</span>
          <Input type="date" value={d.data_recebimento} onChange={(e) => set("data_recebimento", e.target.value)} />
        </label>
        <label style={{ display: "flex", flexDirection: "column", gap: 4 }}>
          <span style={{ fontSize: 12, fontWeight: 600 }}>Bom para (pré-datado) *</span>
          <Input type="date" value={d.bom_para} onChange={(e) => set("bom_para", e.target.value)} />
        </label>
      </div>
      <label style={{ display: "flex", flexDirection: "column", gap: 4 }}>
        <span style={{ fontSize: 12, fontWeight: 600 }}>Banco emissor</span>
        <Input value={d.banco_emissor} onChange={(e) => set("banco_emissor", e.target.value)} placeholder="Ex.: Itaú" />
      </label>
      {d.cliente_id ? (
        <label style={{ display: "flex", flexDirection: "column", gap: 4 }}>
          <span style={{ fontSize: 12, fontWeight: 600 }}>Pedido</span>
          <Select value={d.pedido_id} onChange={(e) => {
            set("pedido_id", e.target.value); set("lancamento_id", "");
          }}>
            <option value="">— nenhum (cheque avulso) —</option>
            {pedidos.map((p) => (
              <option key={p.id} value={p.id}>
                {p.numero} · {formatarReais(p.valor_total)}
              </option>
            ))}
          </Select>
        </label>
      ) : null}
      {d.pedido_id ? (
        <label style={{ display: "flex", flexDirection: "column", gap: 4 }}>
          <span style={{ fontSize: 12, fontWeight: 600 }}>
            Parcela que este cheque paga
          </span>
          <Select
            value={d.lancamento_id}
            onChange={(e) => set("lancamento_id", e.target.value)}
          >
            <option value="">— nenhuma —</option>
            {parcelas.map((l) => (
              <option key={l.id} value={l.id}>
                {l.descricao} · {formatarReais(l.valor)}
                {l.data_competencia ? ` · vence ${ddmm(l.data_competencia)}` : ""}
              </option>
            ))}
          </Select>
          <span style={{ fontSize: 11, opacity: 0.7 }}>
            Sem a parcela, compensar cria uma entrada nova — e se o pedido já
            tem parcela prevista, o mesmo dinheiro conta duas vezes.
          </span>
        </label>
      ) : null}
      <label style={{ display: "flex", flexDirection: "column", gap: 4 }}>
        <span style={{ fontSize: 12, fontWeight: 600 }}>Situação inicial</span>
        <Select value={d.situacao_inicial} onChange={(e) => set("situacao_inicial", e.target.value)}>
          <option value="EM_CAIXA">Em caixa (cofre)</option>
          <option value="DEPOSITADO">Já depositado</option>
        </Select>
      </label>
      {d.situacao_inicial === "DEPOSITADO" ? (
        <label style={{ display: "flex", flexDirection: "column", gap: 4 }}>
          <span style={{ fontSize: 12, fontWeight: 600 }}>Conta do depósito</span>
          <Select
            value={d.conta_deposito_id}
            onChange={(e) => set("conta_deposito_id", e.target.value)}
          >
            <option value="">— escolha a conta —</option>
            {contas.map((c) => (
              <option key={c.id} value={c.id}>{c.nome}</option>
            ))}
          </Select>
        </label>
      ) : null}
      <label style={{ display: "flex", flexDirection: "column", gap: 4 }}>
        <span style={{ fontSize: 12, fontWeight: 600 }}>Observações</span>
        <Input value={d.observacoes} onChange={(e) => set("observacoes", e.target.value)} />
      </label>
      <div style={{ display: "flex", justifyContent: "flex-end", gap: 8 }}>
        <button type="button" className="btn btn--secondary" onClick={onCancel}>Cancelar</button>
        <AsyncButton variant="primary" disabled={!valido}
                     successMessage="Cheque registrado"
                     onClick={() => onSalvar(d)}>Registrar cheque</AsyncButton>
      </div>
    </div>
  );
}


function AcoesCheque({ cheque, onAcao }) {
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
      <div className="money-row" style={{ borderBottom: "1px solid var(--divider)" }}>
        <div className="body">
          <div className="nm">{cheque.cliente_nome}{cheque.pedido_numero ? ` · ${cheque.pedido_numero}` : ""}</div>
          <div className="sub">Nº {cheque.numero_cheque || "—"} · recebido {ddmm(cheque.data_recebimento)} · bom para {ddmm(cheque.bom_para)}</div>
        </div>
        <div className="val">{formatarReais(cheque.valor)}</div>
      </div>
      <div>
        <span className={"badge no-dot " + (TONE[cheque.situacao] || "neutral")}>{cheque.situacao_display}</span>
        {cheque.motivo ? <div className="tiny muted" style={{ marginTop: 6 }}>{cheque.motivo}</div> : null}
      </div>
      <div style={{ display: "flex", flexWrap: "wrap", gap: 8 }}>
        {cheque.pode_depositar && (
          <AsyncButton variant="primary" successMessage="Cheque depositado"
                       onClick={() => onAcao("depositar", cheque)}>Depositar</AsyncButton>
        )}
        {cheque.pode_compensar && (
          <AsyncButton variant="primary" successMessage="Cheque compensado"
                       onClick={() => onAcao("compensar", cheque)}>Compensar</AsyncButton>
        )}
        {cheque.pode_repassar && (
          <AsyncButton variant="secondary" successMessage="Cheque repassado"
                       onClick={() => onAcao("repassar", cheque)}>Repassar a fornecedor</AsyncButton>
        )}
        {cheque.pode_devolver && (
          <AsyncButton variant="danger" successMessage="Cheque marcado como devolvido"
                       onClick={() => onAcao("devolver", cheque)}>Marcar devolvido</AsyncButton>
        )}
        {cheque.pode_sustar && (
          <AsyncButton variant="danger" successMessage="Cheque sustado"
                       onClick={() => onAcao("sustar", cheque)}>Sustar</AsyncButton>
        )}
      </div>
    </div>
  );
}


export default function Cheques() {
  const { request, hasAllPermissions, isMaster } = useAuth();
  const [cheques, setCheques] = useState([]);
  const [resumo, setResumo] = useState(null);
  const [clientes, setClientes] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [tab, setTab] = useState("todos");
  const [novoAberto, setNovoAberto] = useState(false);
  const [selecionado, setSelecionado] = useState(null);
  const [contas, setContas] = useState([]);
  // Escolha da conta no momento de depositar: fica no estado para o painel
  // renderizar o select em vez de um prompt (prompt nao lista contas).
  const [depositando, setDepositando] = useState(null);
  const [contaEscolhida, setContaEscolhida] = useState("");

  const podeGerenciar = isMaster || hasAllPermissions(["cheque:manage"]);

  const carregar = useCallback(async () => {
    setLoading(true); setError(null);
    try {
      const [lista, res] = await Promise.all([
        listarCheques(request, {}),
        obterResumoCheques(request).catch(() => null),
      ]);
      setCheques(Array.isArray(lista) ? lista : lista?.results || []);
      if (res) setResumo(res);
    } catch (err) {
      setError(err?.message || "Erro ao carregar cheques.");
    } finally { setLoading(false); }
  }, [request]);

  useEffect(() => { carregar(); }, [carregar]);

  useEffect(() => {
    listarClientes(request, { pageSize: 500, ordering: "nome" }).then((p) => {
      setClientes(Array.isArray(p) ? p : p?.results || []);
    }).catch(() => {});
    listarContas(request, { ativo: true }).then((p) => {
      setContas(Array.isArray(p) ? p : p?.results || []);
    }).catch(() => setContas([]));
  }, [request]);

  const salvarNovo = useCallback(async (d) => {
    await criarCheque(request, {
      cliente_id: d.cliente_id, valor: d.valor,
      data_recebimento: d.data_recebimento, bom_para: d.bom_para,
      numero_cheque: d.numero_cheque, banco_emissor: d.banco_emissor,
      situacao_inicial: d.situacao_inicial, observacoes: d.observacoes,
      // Sem estes dois o cheque nao movimenta nada: compensar nao baixa a
      // parcela e o dinheiro nao entra em conta nenhuma.
      pedido_id: d.pedido_id || null,
      lancamento_id: d.lancamento_id || null,
      conta_deposito_id: d.conta_deposito_id || null,
    });
    setNovoAberto(false);
    await carregar();
  }, [request, carregar]);

  const executarAcao = useCallback(async (acao, cheque) => {
    if (acao === "depositar") {
      // Sem conta o service registra "entrada nao lancada" no log e o
      // dinheiro do cheque nunca aparece no saldo — por isso a conta e
      // perguntada aqui, e nao assumida.
      setDepositando(cheque);
      setContaEscolhida(contas[0]?.id || "");
      return;
    }
    else if (acao === "compensar") await compensarCheque(request, cheque.id, {});
    else if (acao === "repassar") {
      const fornecedor = window.prompt("Repassar a qual fornecedor?", cheque.fornecedor_nome || "");
      if (!fornecedor) return;
      await repassarCheque(request, cheque.id, { fornecedor_nome: fornecedor });
    } else if (acao === "devolver") {
      const motivo = window.prompt("Motivo da devolução (alínea):", "");
      await devolverCheque(request, cheque.id, { motivo: motivo || "" });
    } else if (acao === "sustar") {
      const motivo = window.prompt("Motivo da sustação:", "");
      await sustarCheque(request, cheque.id, { motivo: motivo || "" });
    }
    setSelecionado(null);
    await carregar();
  }, [request, carregar, contas]);

  const filtro = TABS.find((t) => t.id === tab) || TABS[0];
  const visiveis = useMemo(() => cheques.filter(filtro.match), [cheques, filtro]);

  const kpi = (b) => (resumo?.[b] || { count: 0, total: "0" });

  return (
    <PageContainer style={{ maxWidth: "none" }} className="fin2">
      <div className="flex between" style={{ alignItems: "flex-end", marginBottom: 18, flexWrap: "wrap", gap: 12 }}>
        <div>
          <div className="breadcrumb">
            <span>Início</span><span className="crumb-sep">/</span>
            <span>Financeiro</span><span className="crumb-sep">/</span>
            <span className="active">Cheques</span>
          </div>
          <h1 className="page-title">Controle de Cheques</h1>
          <div className="page-sub">Pré-datados recebidos de clientes e repassados a fornecedores</div>
        </div>
        {podeGerenciar && (
          <button type="button" className="btn btn--primary" onClick={() => setNovoAberto(true)}>
            <FiPlus size={14} aria-hidden="true" /> Novo cheque
          </button>
        )}
      </div>

      <div className="kpi-strip-v2 standalone" style={{ marginBottom: 14 }}>
        <div className="kpi-v2"><div className="lbl">Em caixa</div><div className="val">{formatarReais(kpi("em_caixa").total)}</div><div className="meta">{kpi("em_caixa").count} cheque(s)</div></div>
        <div className="kpi-v2"><div className="lbl">A compensar</div><div className="val" style={{ color: "var(--warning-text)" }}>{formatarReais(kpi("a_compensar").total)}</div><div className="meta">{kpi("a_compensar").count} depositado(s)</div></div>
        <div className="kpi-v2"><div className="lbl">Repassados</div><div className="val">{formatarReais(kpi("repassados").total)}</div><div className="meta">{kpi("repassados").count} a fornecedores</div></div>
        <div className="kpi-v2"><div className="lbl">Sustados / devolvidos</div><div className="val" style={{ color: "var(--danger-text)" }}>{formatarReais(kpi("sustados_devolvidos").total)}</div><div className="meta">{kpi("sustados_devolvidos").count} cheque(s)</div></div>
      </div>

      {error && <div className="empty"><div className="msg"><FiAlertTriangle size={14} aria-hidden="true" /> {error}</div></div>}

      <div className="table-wrap">
        <div className="flex between" style={{ alignItems: "center", padding: "12px 18px", borderBottom: "1px solid var(--border)", gap: 12 }}>
          <div className="tabchip-group">
            {TABS.map((t) => (
              <div key={t.id} className={"tabchip" + (tab === t.id ? " active" : "")}
                   role="button" tabIndex={0} onClick={() => setTab(t.id)}
                   onKeyDown={(e) => e.key === "Enter" && setTab(t.id)}>{t.label}</div>
            ))}
          </div>
          <span className="tiny mono muted">{visiveis.length} CHEQUES</span>
        </div>
        <table className="dtable">
          <colgroup>
            <col style={{ width: 80 }} /><col style={{ width: 70 }} /><col /><col style={{ width: 90 }} />
            <col className="col-money" /><col style={{ width: 80 }} /><col style={{ width: 160 }} /><col style={{ width: 120 }} />
          </colgroup>
          <thead><tr>
            <th>Mês/ano</th><th>Receb.</th><th>Obra / cliente</th><th>Nº cheque</th>
            <th className="num" style={{ textAlign: "right" }}>Valor</th><th>Bom para</th><th>Destino</th><th>Status</th>
          </tr></thead>
          <tbody>
            {visiveis.length === 0 && (
              <tr><td colSpan={8} style={{ padding: 0 }}><div className="empty"><div className="msg">
                {loading ? "Carregando…" : "Nenhum cheque neste filtro."}
              </div></div></td></tr>
            )}
            {visiveis.map((c) => {
              const perigo = c.situacao === "DEVOLVIDO" || c.situacao === "SUSTADO";
              return (
                <tr key={c.id} className="rowlink" style={perigo ? { background: "var(--danger-bg)" } : undefined}
                    onClick={() => setSelecionado(c)}>
                  <td className="mono">{mesAno(c.bom_para)}</td>
                  <td className="mono">{ddmm(c.data_recebimento)}</td>
                  <td className="ell" title={`${c.pedido_numero || ""} ${c.cliente_nome}`.trim()}>
                    {c.pedido_numero ? `${c.pedido_numero} · ` : ""}{c.cliente_nome}
                  </td>
                  <td className="mono">{c.numero_cheque || "—"}</td>
                  <td className="num" style={{ fontWeight: 600 }}>{formatarReais(c.valor)}</td>
                  <td className="mono">{ddmm(c.bom_para)}</td>
                  <td className="ell" title={destinoTexto(c)}>{destinoTexto(c)}</td>
                  <td><span className={"badge no-dot " + (TONE[c.situacao] || "neutral")}>{c.situacao_display}</span></td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      <SlideOverPanel open={novoAberto} onClose={() => setNovoAberto(false)}
                      title="Novo cheque" subtitle="Pré-datado recebido de cliente" width={560}>
        <NovoChequeForm clientes={clientes} contas={contas} onSalvar={salvarNovo} onCancel={() => setNovoAberto(false)} />
      </SlideOverPanel>

      <SlideOverPanel open={!!depositando} onClose={() => setDepositando(null)}
                      title="Depositar cheque"
                      subtitle="Em qual conta o dinheiro entra?" width={460}>
        <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
          <p style={{ margin: 0, fontSize: 13, opacity: 0.8 }}>
            Sem a conta, o depósito é registrado mas o dinheiro não aparece em
            saldo nenhum quando o cheque compensar.
          </p>
          <label style={{ display: "flex", flexDirection: "column", gap: 4 }}>
            <span style={{ fontSize: 12, fontWeight: 600 }}>Conta *</span>
            <Select value={contaEscolhida}
                    onChange={(e) => setContaEscolhida(e.target.value)}>
              <option value="">— escolha a conta —</option>
              {contas.map((c) => (
                <option key={c.id} value={c.id}>{c.nome}</option>
              ))}
            </Select>
          </label>
          <div style={{ display: "flex", justifyContent: "flex-end", gap: 8 }}>
            <button type="button" className="btn btn--secondary"
                    onClick={() => setDepositando(null)}>Cancelar</button>
            <AsyncButton
              variant="primary"
              disabled={!contaEscolhida}
              successMessage="Cheque depositado"
              onClick={async () => {
                await depositarCheque(request, depositando.id, {
                  conta_deposito_id: contaEscolhida,
                });
                setDepositando(null);
                setSelecionado(null);
                await carregar();
              }}
            >
              Depositar
            </AsyncButton>
          </div>
        </div>
      </SlideOverPanel>

      <SlideOverPanel open={!!selecionado} onClose={() => setSelecionado(null)}
                      title={selecionado ? `Cheque ${selecionado.numero}` : ""} width={520}>
        {selecionado && (
          podeGerenciar
            ? <AcoesCheque cheque={selecionado} onAcao={executarAcao} />
            : <div className="empty"><div className="msg">Sem permissão para gerenciar cheques.</div></div>
        )}
      </SlideOverPanel>
    </PageContainer>
  );
}
